/**
 * AnnuairePanel — gestion de l'annuaire des tiers PARTAGÉ (mig 213).
 *
 * Liste filtrable par casquette d'usage + création / édition des casquettes et
 * coordonnées (SANS IBAN — la gestion complète avec IBAN reste en compta).
 * Réutilisable dans n'importe quelle brique (admin, technique…).
 */
import { useEffect, useState } from 'react';
import { Card, Button, Alert } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException } from '@/lib/api';
import { Plus, Search, Lock } from 'lucide-react';
import { ContactActions } from './ContactActions';
import { useAuth } from '@/lib/auth-context';
import type { AnnuaireTiers, TiersCategorieUsage } from '@roronoa-collectivites/shared-types';
import { TIERS_CATEGORIES_USAGE } from '@roronoa-collectivites/shared-types';

type Draft = {
  id?: string; nom: string; email: string; telephone: string; siret: string;
  adresse: string; codePostal: string; ville: string; categoriesUsage: TiersCategorieUsage[];
  reserveAdmin: boolean; reserveBriques: string[];
};
const EMPTY: Draft = { nom: '', email: '', telephone: '', siret: '', adresse: '', codePostal: '', ville: '', categoriesUsage: ['fournisseur_prestataire'], reserveAdmin: false, reserveBriques: [] };

/** Les 8 briques attribuables — même liste que la migration 224. */
const BRIQUES_DEROGATION = [
  { value: 'grc', label: 'GRC & communication' },
  { value: 'service_technique', label: 'Service technique' },
  { value: 'scolaire_cantine', label: 'Scolaire / Cantine' },
  { value: 'associations', label: 'Associations' },
  { value: 'urbanisme', label: 'Urbanisme' },
  { value: 'comptable', label: 'Comptabilité' },
  { value: 'petite_enfance', label: 'Petite enfance' },
  { value: 'rh', label: 'Ressources humaines' },
];

export function AnnuairePanel() {
  const { user } = useAuth();
  // Seul un admin pose ou lève une réservation (le back le vérifie aussi).
  const estAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [items, setItems] = useState<AnnuaireTiers[]>([]);
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState<TiersCategorieUsage | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  async function charger() {
    setError(null);
    try { setItems(await api.annuaire.listTiers({ recherche: recherche || undefined, categorie: filtre || undefined })); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  useEffect(() => { const t = setTimeout(() => void charger(), 250); return () => clearTimeout(t); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [recherche, filtre]);

  function toggleCat(d: Draft, c: TiersCategorieUsage): Draft {
    const has = d.categoriesUsage.includes(c);
    return { ...d, categoriesUsage: has ? d.categoriesUsage.filter((x) => x !== c) : [...d.categoriesUsage, c] };
  }

  async function enregistrer() {
    if (!draft) return;
    if (draft.nom.trim().length < 2) { setError('Nom trop court'); return; }
    if (draft.categoriesUsage.length === 0) { setError('Choisissez au moins une catégorie'); return; }
    setSaving(true); setError(null);
    const body = {
      nom: draft.nom.trim(), email: draft.email.trim() || null, telephone: draft.telephone.trim() || null,
      siret: draft.siret.trim() || null, adresse: draft.adresse.trim() || null,
      codePostal: draft.codePostal.trim() || null, ville: draft.ville.trim() || null,
      categoriesUsage: draft.categoriesUsage,
      reserveAdmin: draft.reserveAdmin,
      reserveBriques: draft.reserveBriques,
    };
    try {
      if (draft.id) await api.annuaire.updateTiers(draft.id, body);
      else await api.annuaire.createTiers(body);
      setMsg(draft.id ? 'Tiers mis à jour.' : 'Tiers créé.');
      setDraft(null); await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }

  const labelOf = (v: TiersCategorieUsage) => TIERS_CATEGORIES_USAGE.find((c) => c.value === v)?.label ?? v;

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <Card variant="glass" padded={false}>
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-ink">Annuaire des tiers</h3>
          <div className="relative ml-auto">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" aria-hidden="true" />
            <input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Rechercher…" className="h-8 w-56 rounded-r-md border border-border bg-surface/60 pl-8 pr-2.5 text-xs text-ink" />
          </div>
          <select value={filtre} onChange={(e) => setFiltre(e.target.value as TiersCategorieUsage | '')} className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink">
            <option value="">Toutes catégories</option>
            {TIERS_CATEGORIES_USAGE.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <Button variant="primary" size="sm" onClick={() => setDraft({ ...EMPTY })}><Plus className="h-4 w-4" /> Ajouter</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-wider text-ink-subtle">
                <th className="px-4 py-2.5 font-medium">Nom</th>
                <th className="px-4 py-2.5 font-medium">Catégories</th>
                <th className="px-4 py-2.5 font-medium">Ville</th>
                <th className="px-4 py-2.5 font-medium">Contact</th>
                <th className="px-4 py-2.5 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-ink-subtle">Aucun tiers.</td></tr>}
              {items.map((t) => (
                <tr key={t.id} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-2.5 text-ink">
                    <span className="flex items-center gap-1.5">
                      {t.nom}
                      {/* Repère visuel : ce contact n'est pas visible des briques. */}
                      {t.reserveAdmin && (
                        <span
                          className="inline-flex items-center gap-1 rounded-r-pill bg-warning/15 px-1.5 py-0.5 text-[9px] uppercase tracking-r-eyebrow text-warning"
                          title={
                            (t.reserveBriques ?? []).length > 0
                              ? `Réservé — visible aussi par les managers : ${(t.reserveBriques ?? []).join(', ')}`
                              : 'Réservé — visible uniquement dans les paramètres'
                          }
                        >
                          <Lock className="h-2.5 w-2.5" aria-hidden="true" />
                          Réservé
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="flex flex-wrap gap-1">
                      {t.categoriesUsage.map((c) => <span key={c} className="rounded-r-pill bg-surface px-1.5 py-0.5 text-[9px] uppercase tracking-r-eyebrow text-ink-subtle">{labelOf(c)}</span>)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-ink-muted">{t.ville ?? '—'}</td>
                  <td className="px-4 py-2.5 text-ink-muted">
                    {t.email || t.telephone ? (
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs">{[t.email, t.telephone].filter(Boolean).join(' · ')}</span>
                        {/*
                          Écrire / appeler / SMS en un clic, sans quitter VPC et
                          sans stocker le moindre identifiant de messagerie :
                          mailto: / tel: / sms: ouvrent l'application déjà
                          choisie par la personne sur son poste ou son mobile.
                        */}
                        <ContactActions nom={t.nom} email={t.email} telephone={t.telephone} compact />
                      </div>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setDraft({
                      id: t.id, nom: t.nom, email: t.email ?? '', telephone: t.telephone ?? '', siret: t.siret ?? '',
                      adresse: t.adresse ?? '', codePostal: t.codePostal ?? '', ville: t.ville ?? '', categoriesUsage: t.categoriesUsage,
                      reserveAdmin: t.reserveAdmin ?? false, reserveBriques: t.reserveBriques ?? [],
                    })}>Éditer</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {draft && (
        <Dialog open onClose={() => setDraft(null)} title={draft.id ? 'Modifier le tiers' : 'Nouveau tiers'} size="md"
          footer={<>
            <Button variant="ghost" onClick={() => setDraft(null)}>Annuler</Button>
            <Button variant="primary" loading={saving} onClick={() => void enregistrer()}>{draft.id ? 'Enregistrer' : 'Créer'}</Button>
          </>}>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Nom *</label>
              <input value={draft.nom} onChange={(e) => setDraft({ ...draft, nom: e.target.value })} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Email</label>
                <input type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" /></div>
              <div><label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Téléphone</label>
                <input value={draft.telephone} onChange={(e) => setDraft({ ...draft, telephone: e.target.value })} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Ville</label>
                <input value={draft.ville} onChange={(e) => setDraft({ ...draft, ville: e.target.value })} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" /></div>
              <div><label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">SIRET</label>
                <input value={draft.siret} onChange={(e) => setDraft({ ...draft, siret: e.target.value })} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" /></div>
            </div>
            {/*
              Contact RÉSERVÉ (mig 224) — réservé à l'admin.

              Un contact coché ici disparaît de TOUTES les briques, y compris du
              filtre « Tout l'annuaire » : c'est le point qui rend la case utile
              (masquer par brique en laissant fuiter la vue globale ne servirait
              à rien).

              Les briques cochées ensuite sont des DÉROGATIONS pour leurs
              MANAGERS : la médecine du travail reste sous la main du manager RH
              sans qu'il ait à sortir de sa brique. Les agents ne la voient
              jamais.
            */}
            {estAdmin && (
              <div className="rounded-r-md border border-border bg-surface/40 p-3">
                <label className="flex cursor-pointer items-start gap-2">
                  <input
                    type="checkbox" checked={draft.reserveAdmin}
                    onChange={(e) => setDraft({ ...draft, reserveAdmin: e.target.checked, reserveBriques: e.target.checked ? draft.reserveBriques : [] })}
                    className="mt-0.5 h-4 w-4 rounded border-border"
                  />
                  <span>
                    <span className="block text-sm font-medium text-ink">Contact réservé</span>
                    <span className="block text-xs text-ink-muted">
                      Masqué dans toutes les briques, y compris « Tout l'annuaire ».
                      Visible ici, dans les paramètres.
                    </span>
                  </span>
                </label>

                {draft.reserveAdmin && (
                  <div className="mt-3 border-t border-border pt-3">
                    <p className="mb-1.5 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                      Visible aussi par les managers de…
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {BRIQUES_DEROGATION.map((b) => {
                        const on = draft.reserveBriques.includes(b.value);
                        return (
                          <button
                            key={b.value} type="button"
                            onClick={() => setDraft({
                              ...draft,
                              reserveBriques: on
                                ? draft.reserveBriques.filter((x) => x !== b.value)
                                : [...draft.reserveBriques, b.value],
                            })}
                            className={`rounded-r-pill px-2.5 py-1 text-xs font-medium transition-colors ${on ? 'bg-tech text-white' : 'border border-border text-ink-muted hover:bg-surface/60'}`}
                          >
                            {b.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-xs text-ink-subtle">
                      Les agents ne voient jamais un contact réservé, même dans une brique cochée.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Catégories (au moins une)</label>
              <div className="flex flex-wrap gap-1.5">
                {TIERS_CATEGORIES_USAGE.map((c) => {
                  const on = draft.categoriesUsage.includes(c.value);
                  return (
                    <button key={c.value} type="button" onClick={() => setDraft(toggleCat(draft, c.value))}
                      className={`rounded-r-pill px-2.5 py-1 text-xs font-medium transition-colors ${on ? 'bg-tech text-white' : 'border border-border text-ink-muted hover:bg-surface/60'}`}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
